import React, { useState, useEffect } from 'react';

const API_BASE = process.env.REACT_APP_API_URL || '/api';

const GestionNotifications = () => {
  const [templates, setTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [loading, setLoading] = useState(true);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [logoUrl, setLogoUrl] = useState('/images/logo-ferme.png'); // Logo de la ferme

  // Templates prédéfinis
  const templateTypes = [
    { id: 'confirmation_commande', label: '✅ Confirmation Commande', icon: '✅' },
    { id: 'modification_commande', label: '🔄 Modification Commande', icon: '🔄' },
    { id: 'annulation_commande', label: '❌ Annulation Commande', icon: '❌' },
    { id: 'rappel_retrait', label: '⏰ Rappel Retrait', icon: '⏰' },
    { id: 'commande_prete', label: '📦 Commande Prête', icon: '📦' },
    { id: 'defaut_livraison', label: '⚠️ Défaut Livraison', icon: '⚠️' },
    { id: 'promotion', label: '🎁 Promotion', icon: '🎁' },
    { id: 'stock_epuise', label: '🚫 Stock Épuisé', icon: '🚫' },
    { id: 'ouverture_reservations', label: '🔓 Ouverture Réservations', icon: '🔓' },
    { id: 'fermeture_exceptionnelle', label: '🔒 Fermeture Exceptionnelle', icon: '🔒' }
  ];

  // Variables dynamiques disponibles
  const variables = [
    { code: '{{nom_client}}', desc: 'Nom du client' },
    { code: '{{prenom_client}}', desc: 'Prénom du client' },
    { code: '{{nom_ferme}}', desc: 'Nom de la ferme' },
    { code: '{{date_livraison}}', desc: 'Date de livraison' },
    { code: '{{numero_commande}}', desc: 'Numéro de commande' },
    { code: '{{montant_total}}', desc: 'Montant total' },
    { code: '{{liste_produits}}', desc: 'Liste des produits (tableau détaillé)' },
    { code: '{{lien_commande}}', desc: 'Lien vers la commande' },
    { code: '{{adresse_ferme}}', desc: 'Adresse de la ferme' },
    { code: '{{telephone_ferme}}', desc: 'Téléphone de la ferme' }
  ];

  // Données de test pour la prévisualisation
  const mockData = {
    nom_client: 'Dupont',
    prenom_client: 'Jean',
    nom_ferme: 'La Ferme du Petit Essart',
    date_livraison: '15 novembre 2025',
    numero_commande: 'CMD-2025-1234',
    montant_total: '45.80 €',
    adresse_ferme: '123 Route de la Campagne, 78000 Versailles',
    telephone_ferme: '01 23 45 67 89',
    lien_commande: 'https://farm-booking.fr/commande/CMD-2025-1234',
    produits: [
      {
        nom: 'Carottes Bio',
        quantite: 2,
        unite: 'kg',
        prix_unitaire: 3.50,
        montant: 7.00,
        image_url: '/images/carottes.jpg',
        video_url: null
      },
      {
        nom: 'Pommes de Terre',
        quantite: 5,
        unite: 'kg',
        prix_unitaire: 2.80,
        montant: 14.00,
        image_url: '/images/pommes-terre.jpg',
        video_url: 'https://youtube.com/watch?v=abc123'
      },
      {
        nom: 'Salade Verte',
        quantite: 3,
        unite: 'pièce',
        prix_unitaire: 1.50,
        montant: 4.50,
        image_url: null,
        video_url: null
      }
    ]
  };

  useEffect(() => {
    loadTemplates();
  }, []);

  const loadTemplates = async () => {
    try {
      const response = await fetch(`${API_BASE}/email-templates`);
      const data = await response.json();
      setTemplates(data);
      setLoading(false);
    } catch (error) {
      console.error('Erreur chargement templates:', error);
      setLoading(false);
    }
  };

  // Générer le template HTML de base avec logo
  const getBaseHTMLTemplate = () => {
    return `
<!DOCTYPE html>
<html lang="fr">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <style>
    body {
      font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
      line-height: 1.6;
      color: #333;
      background-color: #f5f5f5;
      margin: 0;
      padding: 0;
    }
    .email-container {
      max-width: 600px;
      margin: 20px auto;
      background-color: #ffffff;
      border-radius: 8px;
      overflow: hidden;
      box-shadow: 0 2px 8px rgba(0,0,0,0.1);
    }
    .header {
      background: linear-gradient(135deg, #4CAF50 0%, #2E7D32 100%);
      color: white;
      padding: 30px 20px;
      text-align: center;
    }
    .logo {
      max-width: 150px;
      height: auto;
      margin-bottom: 15px;
    }
    .header h1 {
      margin: 0;
      font-size: 24px;
      font-weight: 600;
    }
    .content {
      padding: 30px 20px;
    }
    .greeting {
      font-size: 18px;
      color: #2E7D32;
      margin-bottom: 20px;
    }
    .message {
      margin-bottom: 25px;
      color: #555;
    }
    .info-box {
      background-color: #E8F5E9;
      border-left: 4px solid #4CAF50;
      padding: 15px;
      margin: 20px 0;
      border-radius: 4px;
    }
    .info-box strong {
      color: #2E7D32;
    }
    .products-table {
      width: 100%;
      border-collapse: collapse;
      margin: 20px 0;
      background-color: #fff;
    }
    .products-table thead {
      background-color: #4CAF50;
      color: white;
    }
    .products-table th,
    .products-table td {
      padding: 12px;
      text-align: left;
      border-bottom: 1px solid #e0e0e0;
    }
    .products-table th {
      font-weight: 600;
      text-transform: uppercase;
      font-size: 12px;
      letter-spacing: 0.5px;
    }
    .products-table tbody tr:hover {
      background-color: #f5f5f5;
    }
    .products-table .price {
      text-align: right;
      font-weight: 600;
      color: #2E7D32;
    }
    .total-row {
      background-color: #E8F5E9;
      font-weight: bold;
      font-size: 16px;
    }
    .total-row td {
      padding: 15px 12px;
      border-top: 2px solid #4CAF50;
    }
    .media-section {
      margin-top: 30px;
      padding: 20px;
      background-color: #f9f9f9;
      border-radius: 8px;
    }
    .media-section h3 {
      color: #2E7D32;
      margin-top: 0;
      margin-bottom: 15px;
      font-size: 16px;
    }
    .media-grid {
      display: grid;
      grid-template-columns: repeat(auto-fill, minmax(150px, 1fr));
      gap: 15px;
    }
    .media-item {
      text-align: center;
    }
    .media-item img {
      width: 100%;
      height: 120px;
      object-fit: cover;
      border-radius: 8px;
      border: 2px solid #e0e0e0;
    }
    .media-item a {
      display: inline-block;
      margin-top: 8px;
      color: #4CAF50;
      text-decoration: none;
      font-size: 14px;
    }
    .media-item a:hover {
      text-decoration: underline;
    }
    .button {
      display: inline-block;
      background-color: #4CAF50;
      color: white;
      padding: 12px 30px;
      text-decoration: none;
      border-radius: 5px;
      font-weight: 600;
      margin: 20px 0;
      transition: background-color 0.3s;
    }
    .button:hover {
      background-color: #2E7D32;
    }
    .footer {
      background-color: #f5f5f5;
      padding: 20px;
      text-align: center;
      font-size: 14px;
      color: #777;
      border-top: 1px solid #e0e0e0;
    }
    .footer a {
      color: #4CAF50;
      text-decoration: none;
    }
    .footer a:hover {
      text-decoration: underline;
    }
  </style>
</head>
<body>
  <div class="email-container">
    <div class="header">
      <img src="${logoUrl}" alt="Logo" class="logo" />
      <h1>{{nom_ferme}}</h1>
    </div>
    <div class="content">
      <div class="greeting">
        Bonjour {{prenom_client}} {{nom_client}},
      </div>
      
      <!-- CONTENU PERSONNALISÉ ICI -->
      
    </div>
    <div class="footer">
      <p>
        <strong>{{nom_ferme}}</strong><br/>
        {{adresse_ferme}}<br/>
        Tél: {{telephone_ferme}}
      </p>
      <p>
        <a href="{{lien_commande}}">Voir ma commande en ligne</a>
      </p>
    </div>
  </div>
</body>
</html>`;
  };

  // Générer la liste de produits en HTML
  const generateProductsTableHTML = (produits) => {
    const rows = produits.map(p => `
      <tr>
        <td>${p.nom}</td>
        <td style="text-align: center;">${p.quantite} ${p.unite}</td>
        <td class="price">${p.prix_unitaire.toFixed(2)} €</td>
        <td class="price">${p.montant.toFixed(2)} €</td>
      </tr>
    `).join('');

    const total = produits.reduce((sum, p) => sum + p.montant, 0);

    const mediasHtml = generateMediaSectionHTML(produits);

    return `
      <table class="products-table">
        <thead>
          <tr>
            <th>Produit</th>
            <th style="text-align: center;">Quantité</th>
            <th style="text-align: right;">Prix Unit.</th>
            <th style="text-align: right;">Montant</th>
          </tr>
        </thead>
        <tbody>
          ${rows}
          <tr class="total-row">
            <td colspan="3" style="text-align: right;">TOTAL</td>
            <td class="price">${total.toFixed(2)} €</td>
          </tr>
        </tbody>
      </table>
      ${mediasHtml}
    `;
  };

  // Générer la section médias (images/vidéos)
  const generateMediaSectionHTML = (produits) => {
    const mediaProduits = produits.filter(p => p.image_url || p.video_url);
    
    if (mediaProduits.length === 0) return '';

    const mediaItems = mediaProduits.map(p => {
      let html = '<div class="media-item">';
      
      if (p.image_url) {
        html += `
          <img src="${p.image_url}" alt="${p.nom}" />
          <div style="font-size: 12px; margin-top: 5px; color: #666;">${p.nom}</div>
        `;
      }
      
      if (p.video_url) {
        html += `
          <a href="${p.video_url}" target="_blank">🎥 Voir la vidéo</a>
        `;
      }
      
      html += '</div>';
      return html;
    }).join('');

    return `
      <div class="media-section">
        <h3>📸 Photos et Vidéos de vos produits</h3>
        <div class="media-grid">
          ${mediaItems}
        </div>
      </div>
    `;
  };

  // Templates HTML complets pour chaque type de notification
  const getTemplateContent = (type) => {
    const templates = {
      confirmation_commande: `
        <div class="message">
          <p>Nous avons bien reçu votre commande n°<strong>{{numero_commande}}</strong>.</p>
          <p>Vous pourrez la récupérer le <strong>{{date_livraison}}</strong>.</p>
        </div>
        
        <div class="info-box">
          <strong>📋 Récapitulatif de votre commande :</strong>
        </div>
        
        {{liste_produits}}
        
        <a href="{{lien_commande}}" class="button">Voir ma commande</a>
        
        <p style="color: #666; font-size: 14px;">
          Merci de votre confiance ! Si vous avez des questions, n'hésitez pas à nous contacter.
        </p>
      `,
      
      modification_commande: `
        <div class="message">
          <p>Votre commande n°<strong>{{numero_commande}}</strong> a été modifiée.</p>
          <p>Date de retrait : <strong>{{date_livraison}}</strong></p>
        </div>
        
        <div class="info-box">
          <strong>📋 Nouvelle composition de votre commande :</strong>
        </div>
        
        {{liste_produits}}
        
        <a href="{{lien_commande}}" class="button">Voir les modifications</a>
        
        <p style="color: #666; font-size: 14px;">
          Si cette modification ne vous convient pas, contactez-nous rapidement.
        </p>
      `,
      
      annulation_commande: `
        <div class="message">
          <p>Votre commande n°<strong>{{numero_commande}}</strong> a été annulée.</p>
        </div>
        
        <div class="info-box" style="background-color: #FFF3E0; border-left-color: #FF9800;">
          <strong>ℹ️ Détails de la commande annulée :</strong>
        </div>
        
        {{liste_produits}}
        
        <p style="color: #666; font-size: 14px;">
          Si vous souhaitez passer une nouvelle commande, nous sommes à votre disposition.
        </p>
      `,
      
      rappel_retrait: `
        <div class="message">
          <p>⏰ <strong>Rappel important !</strong></p>
          <p>Votre commande n°<strong>{{numero_commande}}</strong> est à retirer <strong>{{date_livraison}}</strong>.</p>
        </div>
        
        <div class="info-box">
          <strong>📋 Rappel de votre commande :</strong>
        </div>
        
        {{liste_produits}}
        
        <p style="color: #666; font-size: 14px;">
          Pensez à venir avec vos contenants réutilisables ! 🌱
        </p>
      `,
      
      commande_prete: `
        <div class="message">
          <p>📦 <strong>Bonne nouvelle !</strong></p>
          <p>Votre commande n°<strong>{{numero_commande}}</strong> est prête à être récupérée.</p>
          <p>Vous pouvez venir la chercher dès maintenant !</p>
        </div>
        
        <div class="info-box">
          <strong>📋 Votre commande :</strong>
        </div>
        
        {{liste_produits}}
        
        <p style="color: #666; font-size: 14px;">
          Nous vous attendons avec plaisir ! 😊
        </p>
      `,
      
      defaut_livraison: `
        <div class="message">
          <p>⚠️ <strong>Information importante</strong></p>
          <p>Nous rencontrons un problème avec votre commande n°<strong>{{numero_commande}}</strong>.</p>
          <p>Certains produits ne sont pas disponibles dans les quantités prévues.</p>
        </div>
        
        <div class="info-box" style="background-color: #FFEBEE; border-left-color: #F44336;">
          <strong>📋 État de votre commande :</strong>
        </div>
        
        {{liste_produits}}
        
        <p style="color: #666; font-size: 14px;">
          Nous vous contacterons rapidement pour trouver une solution. Merci de votre compréhension.
        </p>
      `,
      
      promotion: `
        <div class="message">
          <p>🎁 <strong>Offre spéciale rien que pour vous !</strong></p>
          <p>Profitez de nos produits de saison à prix réduits.</p>
        </div>
        
        <div class="info-box">
          <strong>🌟 Produits en promotion :</strong>
        </div>
        
        {{liste_produits}}
        
        <a href="{{lien_commande}}" class="button">Commander maintenant</a>
        
        <p style="color: #666; font-size: 14px;">
          Offre valable jusqu'à épuisement des stocks.
        </p>
      `,
      
      stock_epuise: `
        <div class="message">
          <p>Nous sommes désolés, mais certains produits de votre commande n°<strong>{{numero_commande}}</strong> ne sont plus disponibles.</p>
        </div>
        
        <div class="info-box" style="background-color: #FFF3E0; border-left-color: #FF9800;">
          <strong>⚠️ Produits concernés :</strong>
        </div>
        
        {{liste_produits}}
        
        <p style="color: #666; font-size: 14px;">
          Nous vous proposons de modifier votre commande ou de la reporter à une date ultérieure.
        </p>
      `,
      
      ouverture_reservations: `
        <div class="message">
          <p>🔓 <strong>Les réservations sont ouvertes !</strong></p>
          <p>Découvrez nos nouveaux produits de saison et réservez dès maintenant.</p>
        </div>
        
        <div class="info-box">
          <strong>🌱 Produits disponibles :</strong>
        </div>
        
        {{liste_produits}}
        
        <a href="{{lien_commande}}" class="button">Réserver maintenant</a>
        
        <p style="color: #666; font-size: 14px;">
          Dépêchez-vous, les quantités sont limitées !
        </p>
      `,
      
      fermeture_exceptionnelle: `
        <div class="message">
          <p>🔒 <strong>Information importante</strong></p>
          <p>La ferme sera exceptionnellement fermée le <strong>{{date_livraison}}</strong>.</p>
          <p>Nous vous invitons à récupérer votre commande avant cette date.</p>
        </div>
        
        <div class="info-box" style="background-color: #FFF3E0; border-left-color: #FF9800;">
          <strong>📋 Votre commande en cours :</strong>
        </div>
        
        {{liste_produits}}
        
        <p style="color: #666; font-size: 14px;">
          Merci de votre compréhension. Nous restons à votre disposition pour toute question.
        </p>
      `
    };

    return templates[type] || templates.confirmation_commande;
  };

  // Remplacer les variables par les données de test
  const replaceVariables = (html, data) => {
    let result = html;
    
    // Remplacement des variables simples
    Object.keys(data).forEach(key => {
      if (key !== 'produits') {
        const regex = new RegExp(`{{${key}}}`, 'g');
        result = result.replace(regex, data[key] || '');
      }
    });

    // Remplacement de la liste de produits
    if (result.includes('{{liste_produits}}')) {
      const productsHTML = generateProductsTableHTML(data.produits);
      result = result.replace('{{liste_produits}}', productsHTML);
    }

    return result;
  };

  // Générer l'aperçu HTML complet
  const generatePreviewHTML = () => {
    if (!selectedTemplate) return '';

    const baseTemplate = getBaseHTMLTemplate();
    const content = selectedTemplate.contenu_html || getTemplateContent(selectedTemplate.type);
    
    // Insérer le contenu dans le template de base
    const fullHTML = baseTemplate.replace('<!-- CONTENU PERSONNALISÉ ICI -->', content);
    
    // Remplacer les variables par les données de test
    return replaceVariables(fullHTML, mockData);
  };

  const handleNewTemplate = (type) => {
    const defaultContent = getTemplateContent(type.id);
    
    setSelectedTemplate({
      type: type.id,
      label: type.label,
      sujet: `${type.label} - {{nom_ferme}}`,
      contenu_html: defaultContent,
      image_url: '',
      video_url: '',
      actif: true
    });
    setEditMode(true);
  };

  const handleSave = async () => {
    try {
      const method = selectedTemplate.id ? 'PUT' : 'POST';
      const url = selectedTemplate.id 
        ? `${API_BASE}/email-templates/${selectedTemplate.id}`
        : `${API_BASE}/email-templates`;

      await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(selectedTemplate)
      });

      alert('✅ Template sauvegardé !');
      loadTemplates();
      setEditMode(false);
    } catch (error) {
      console.error('Erreur sauvegarde:', error);
      alert('❌ Erreur lors de la sauvegarde');
    }
  };

  // 📧 Envoyer un email de test
  const handleSendTest = async (email) => {
    if (!email || !email.includes('@')) {
      alert('Email invalide !');
      return;
    }

    try {
      const response = await fetch(`${API_BASE}/email-templates/send-test`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: email,
          template: {
            ...selectedTemplate,
            html: generatePreviewHTML() // Envoyer le HTML complet
          }
        })
      });

      const data = await response.json();

      if (data.success) {
        alert(`✅ Email de test envoyé à ${email} !`);
      } else {
        alert(`❌ Erreur: ${data.message}`);
      }
    } catch (error) {
      console.error('Erreur envoi test:', error);
      alert('❌ Erreur lors de l\'envoi du test');
    }
  };

  const insertVariable = (variable) => {
    const textarea = document.getElementById('contenu-email');
    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const text = selectedTemplate.contenu_html;
    const newText = text.substring(0, start) + variable + text.substring(end);
    
    setSelectedTemplate(prev => ({
      ...prev,
      contenu_html: newText
    }));
  };

  const uploadLogo = async (file) => {
    const formData = new FormData();
    formData.append('image', file);

    try {
      const response = await fetch(`${API_BASE}/upload/logo`, {
        method: 'POST',
        body: formData
      });
      const data = await response.json();
      
      setLogoUrl(data.url);
      alert('✅ Logo mis à jour !');
    } catch (error) {
      console.error('Erreur upload:', error);
      alert('❌ Erreur lors de l\'upload du logo');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="text-gray-500">Chargement des templates...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-xl font-semibold text-gray-900">📧 Gestion des Notifications Email</h2>
        <div className="flex space-x-2">
          {/* Upload du logo */}
          <div className="relative">
            <input
              type="file"
              accept="image/*"
              onChange={(e) => uploadLogo(e.target.files[0])}
              className="hidden"
              id="logo-upload"
            />
            <label
              htmlFor="logo-upload"
              className="bg-purple-600 text-white px-4 py-2 rounded-md hover:bg-purple-700 cursor-pointer inline-block"
            >
              🖼️ Logo Ferme
            </label>
          </div>
          
          {editMode && (
            <>
              <button
                onClick={() => setPreviewOpen(true)}
                className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 font-medium"
              >
                👁️ Prévisualiser HTML
              </button>
              <button
                onClick={handleSave}
                className="bg-green-600 text-white px-4 py-2 rounded-md hover:bg-green-700 font-medium"
              >
                💾 Sauvegarder
              </button>
              <button
                onClick={() => {
                  setEditMode(false);
                  setSelectedTemplate(null);
                }}
                className="bg-gray-500 text-white px-4 py-2 rounded-md hover:bg-gray-600"
              >
                ❌ Annuler
              </button>
            </>
          )}
        </div>
      </div>

      {!editMode ? (
        // Liste des templates
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {templateTypes.map(type => {
            const existing = templates.find(t => t.type === type.id);
            
            return (
              <div
                key={type.id}
                className={`bg-white shadow rounded-lg p-6 cursor-pointer transition-all hover:shadow-lg ${
                  existing ? 'border-2 border-green-500' : 'border-2 border-gray-200'
                }`}
                onClick={() => {
                  if (existing) {
                    setSelectedTemplate(existing);
                    setEditMode(true);
                  } else {
                    handleNewTemplate(type);
                  }
                }}
              >
                <div className="flex items-center justify-between mb-4">
                  <span className="text-3xl">{type.icon}</span>
                  {existing && (
                    <span className="bg-green-100 text-green-800 text-xs px-2 py-1 rounded">
                      Configuré
                    </span>
                  )}
                </div>
                <h3 className="text-lg font-medium text-gray-900">{type.label}</h3>
                <p className="text-sm text-gray-500 mt-2">
                  {existing ? 'Cliquez pour modifier' : 'Cliquez pour créer'}
                </p>
              </div>
            );
          })}
        </div>
      ) : (
        // Éditeur de template
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Zone d'édition */}
          <div className="lg:col-span-2 space-y-4">
            <div className="bg-white shadow rounded-lg p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                {selectedTemplate.label}
              </h3>

              {/* Sujet */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  📝 Sujet de l'email
                </label>
                <input
                  type="text"
                  value={selectedTemplate.sujet || ''}
                  onChange={(e) => setSelectedTemplate(prev => ({
                    ...prev,
                    sujet: e.target.value
                  }))}
                  className="w-full p-2 border rounded-md focus:ring-green-500 focus:border-green-500"
                  placeholder="Ex: Votre commande est confirmée !"
                />
              </div>

              {/* Contenu HTML */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  ✏️ Contenu de l'email (HTML)
                </label>
                <textarea
                  id="contenu-email"
                  value={selectedTemplate.contenu_html || ''}
                  onChange={(e) => setSelectedTemplate(prev => ({
                    ...prev,
                    contenu_html: e.target.value
                  }))}
                  className="w-full p-3 border rounded-md h-96 font-mono text-sm focus:ring-green-500 focus:border-green-500"
                  placeholder="Le contenu HTML est pré-rempli avec un modèle professionnel.
Vous pouvez le personnaliser en utilisant les variables disponibles."
                />
              </div>

              {/* Actif/Inactif */}
              <div className="flex items-center">
                <input
                  type="checkbox"
                  checked={selectedTemplate.actif}
                  onChange={(e) => setSelectedTemplate(prev => ({
                    ...prev,
                    actif: e.target.checked
                  }))}
                  className="mr-2"
                  id="template-actif"
                />
                <label htmlFor="template-actif" className="text-sm text-gray-700">
                  Template actif (sera utilisé pour l'envoi automatique)
                </label>
              </div>
            </div>
          </div>

          {/* Sidebar - Variables & Test */}
          <div className="space-y-4">
            {/* 📧 TEST EMAIL */}
            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
              <h4 className="font-medium text-green-900 mb-3">📧 Test Email</h4>
              <div className="space-y-2">
                <input
                  type="email"
                  id="test-email"
                  placeholder="email@test.fr"
                  className="w-full p-2 border rounded-md text-sm"
                />
                <button
                  onClick={() => {
                    const email = document.getElementById('test-email').value;
                    handleSendTest(email);
                  }}
                  className="w-full bg-green-600 text-white px-3 py-2 rounded-md hover:bg-green-700 text-sm font-medium"
                >
                  🚀 Envoyer Test
                </button>
              </div>
              <p className="text-xs text-green-700 mt-2">
                Envoyez un email de test avec le rendu HTML complet
              </p>
            </div>

            <div className="bg-white shadow rounded-lg p-4">
              <h4 className="font-medium text-gray-900 mb-3">🏷️ Variables Disponibles</h4>
              <div className="space-y-2 max-h-96 overflow-y-auto">
                {variables.map(variable => (
                  <button
                    key={variable.code}
                    onClick={() => insertVariable(variable.code)}
                    className="w-full text-left p-2 hover:bg-gray-100 rounded text-sm transition-colors"
                  >
                    <code className="text-blue-600 font-mono text-xs">
                      {variable.code}
                    </code>
                    <div className="text-gray-600 text-xs mt-1">
                      {variable.desc}
                    </div>
                  </button>
                ))}
              </div>
            </div>

            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <h4 className="font-medium text-blue-900 mb-2">💡 Conseils</h4>
              <ul className="text-sm text-blue-700 space-y-1">
                <li>• Le template HTML inclut déjà le logo</li>
                <li>• La variable liste_produits génère un tableau complet</li>
                <li>• Images et vidéos s'affichent automatiquement</li>
                <li>• Testez avant d'activer !</li>
              </ul>
            </div>
          </div>
        </div>
      )}

      {/* Modal Prévisualisation HTML COMPLÈTE */}
      {previewOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
            <div className="p-4 border-b flex justify-between items-center">
              <h3 className="text-lg font-semibold">👁️ Prévisualisation HTML Complète</h3>
              <button
                onClick={() => setPreviewOpen(false)}
                className="text-gray-500 hover:text-gray-700 text-2xl"
              >
                ×
              </button>
            </div>
            
            <div className="flex-1 overflow-y-auto">
              <iframe
                srcDoc={generatePreviewHTML()}
                className="w-full h-full border-0"
                style={{ minHeight: '600px' }}
                title="Prévisualisation Email"
              />
            </div>

            <div className="p-4 border-t bg-gray-50">
              <p className="text-sm text-gray-600">
                ℹ️ Ceci est un aperçu avec des données de test. Le rendu final utilisera les vraies données de commande.
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default GestionNotifications;